import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Plus, X, Trash2, Pencil, Settings, Cake, Bell, BellRing } from "lucide-react";
import {
  criarEventoCalendario,
  atualizarEventoCalendario,
  deletarEventoCalendario,
  listarAniversarios,
  EventoCalendario,
  TipoEvento,
  EVENTO_VISUAL,
  getLembreteAtivo,
  setLembreteAtivo,
  getLembreteAntecedencia,
  setLembreteAntecedencia,
  ANTECEDENCIA_OPCOES,
} from "../services/calendario";

/**
 * Peças dos EVENTOS DA EMPRESA (aniversário, feriado, reunião, treinamento…).
 * A tela própria de "Eventos" deixou de existir em jul/2026: os eventos passaram
 * a ser desenhados no mesmo calendário dos marcos de projeto, dentro da aba
 * Cronograma (pedido da Rec — "juntar tudo no mesmo filtro"). Este arquivo virou
 * a casa dos modais/chips que o Cronograma reaproveita.
 */
export const TIPOS_LISTA: TipoEvento[] = ["aniversario", "feriado", "reuniao", "treinamento", "podcast", "outro"];

export function FiltroTipoChip({ ativo, onClick, children }: { ativo: boolean; onClick: () => void; children: React.ReactNode }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`inline-flex items-center gap-1.5 text-xs font-medium px-2.5 py-1.5 rounded-full border transition-colors ${
        ativo
          ? "bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300 border-blue-300 dark:border-blue-700"
          : "bg-white dark:bg-slate-800 text-slate-500 dark:text-slate-400 border-slate-200 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-700/50"
      }`}
    >
      {children}
    </button>
  );
}

export function EventoModal({ inicial, modoEdicao, onClose, onSucesso }: {
  inicial: Partial<EventoCalendario> & { data: string };
  modoEdicao: boolean;
  onClose: () => void;
  onSucesso: () => void;
}) {
  const [titulo, setTitulo] = useState(inicial.titulo || "");
  const [descricao, setDescricao] = useState(inicial.descricao || "");
  const [data, setData] = useState(inicial.data);
  const [hora, setHora] = useState(inicial.hora || "");
  const [tipo, setTipo] = useState<TipoEvento>((inicial.tipo as TipoEvento) || "outro");
  const [recorrenteAnual, setRecorrenteAnual] = useState(!!inicial.recorrenteAnual);

  const ehFeriadoExterno = inicial.origem === "feriado-api";

  // Combina data + hora num ISO datetime local. Se hora vazia, usa 00:00 (= "dia inteiro").
  function dataIsoCombinada(): string {
    const h = hora && /^\d{2}:\d{2}$/.test(hora) ? hora : "00:00";
    return `${data}T${h}:00`;
  }

  const criarM = useMutation({
    mutationFn: () => criarEventoCalendario({ titulo, descricao: descricao || null, data: dataIsoCombinada(), tipo, recorrenteAnual }),
    onSuccess: onSucesso,
  });
  const editarM = useMutation({
    mutationFn: () => atualizarEventoCalendario(inicial.id!, { titulo, descricao: descricao || null, data: dataIsoCombinada(), tipo, recorrenteAnual }),
    onSuccess: onSucesso,
  });
  const deletarM = useMutation({
    mutationFn: () => deletarEventoCalendario(inicial.id!),
    onSuccess: onSucesso,
  });

  const submitting = criarM.isPending || editarM.isPending;
  const podeSalvar = titulo.trim().length > 0 && !!data;

  // Surface erro real do backend (validação Zod, etc.) pra debug visual.
  const erroMsg = (() => {
    const e: any = criarM.error || editarM.error || deletarM.error;
    const apiErr = e?.response?.data?.error;
    if (!apiErr) return null;
    if (apiErr.issues && typeof apiErr.issues === "object") {
      const linhas: string[] = [];
      for (const [campo, msgs] of Object.entries(apiErr.issues)) {
        if (Array.isArray(msgs) && msgs.length) linhas.push(`${campo}: ${msgs.join(", ")}`);
      }
      if (linhas.length) return linhas.join(" | ");
    }
    return apiErr.message || null;
  })();

  function handleSubmit() {
    if (modoEdicao) editarM.mutate();
    else criarM.mutate();
  }
  function handleDeletar() {
    if (!inicial.id) return;
    if (window.confirm(`Excluir o evento "${titulo}"?`)) deletarM.mutate();
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={onClose}>
      <div className="bg-white dark:bg-slate-800 rounded-xl w-full max-w-md shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="px-5 py-3 border-b border-slate-200 dark:border-slate-700 flex items-center justify-between">
          <h3 className="font-semibold text-slate-800 dark:text-slate-100">
            {modoEdicao ? "Editar evento" : "Novo evento"}
          </h3>
          <button onClick={onClose} className="p-1.5 rounded hover:bg-slate-100 dark:hover:bg-slate-700 text-slate-500 dark:text-slate-400">
            <X size={18} />
          </button>
        </div>

        {ehFeriadoExterno && (
          <div className="mx-5 mt-3 p-2.5 rounded-lg bg-amber-50 dark:bg-amber-900/30 border border-amber-200 dark:border-amber-700 text-xs text-amber-800 dark:text-amber-200">
            Feriado nacional importado da BrasilAPI — não pode ser editado nem excluído.
            Para sobrescrever, crie um novo evento na mesma data.
          </div>
        )}

        <div className="p-5 space-y-3">
          <div>
            <label className="text-xs text-slate-600 dark:text-slate-400">Tipo</label>
            <div className="grid grid-cols-3 sm:grid-cols-6 gap-1.5 mt-1">
              {TIPOS_LISTA.map((t) => {
                const v = EVENTO_VISUAL[t];
                const ativo = tipo === t;
                return (
                  <button
                    key={t}
                    type="button"
                    onClick={() => setTipo(t)}
                    className={`flex flex-col items-center gap-1 p-2 rounded-lg border text-[11px] ${
                      ativo
                        ? `${v.cor} font-semibold`
                        : "bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700 text-slate-500 dark:text-slate-400 hover:bg-slate-50 dark:hover:bg-slate-700"
                    }`}
                  >
                    <span className="text-base">{v.emoji}</span>
                    <span className="truncate w-full text-center">{v.label}</span>
                  </button>
                );
              })}
            </div>
          </div>

          <div>
            <label className="text-xs text-slate-600 dark:text-slate-400">
              Título <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={titulo}
              onChange={(e) => setTitulo(e.target.value)}
              placeholder={tipo === "aniversario" ? "Ex.: Aniversário Diogo" : "Ex.: Reunião de planejamento"}
              className="w-full px-3 py-2 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-800 dark:text-slate-100 focus:border-blue-500 outline-none text-sm"
            />
          </div>

          <div className="grid grid-cols-12 gap-2">
            <div className="col-span-7">
              <label className="text-xs text-slate-600 dark:text-slate-400">
                Data <span className="text-red-500">*</span>
              </label>
              <input
                type="date"
                value={data}
                onChange={(e) => setData(e.target.value)}
                className="w-full px-3 py-2 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-800 dark:text-slate-100 focus:border-blue-500 outline-none text-sm"
              />
            </div>
            <div className="col-span-5">
              <label className="text-xs text-slate-600 dark:text-slate-400">
                Hora <span className="text-slate-400">(opcional)</span>
              </label>
              <div className="flex gap-1">
                <input
                  type="time"
                  value={hora}
                  onChange={(e) => setHora(e.target.value)}
                  className="flex-1 px-3 py-2 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-800 dark:text-slate-100 focus:border-blue-500 outline-none text-sm"
                />
                {hora && (
                  <button
                    type="button"
                    onClick={() => setHora("")}
                    className="px-2 rounded-lg border border-slate-300 dark:border-slate-600 text-slate-400 dark:text-slate-500 hover:text-slate-700 dark:hover:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-700"
                    title="Sem horário (dia inteiro)"
                  >
                    <X size={14} />
                  </button>
                )}
              </div>
            </div>
          </div>

          <div>
            <label className="text-xs text-slate-600 dark:text-slate-400">Descrição (opcional)</label>
            <textarea
              value={descricao}
              onChange={(e) => setDescricao(e.target.value)}
              rows={2}
              placeholder="Detalhes adicionais"
              className="w-full px-3 py-2 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-800 dark:text-slate-100 focus:border-blue-500 outline-none text-sm"
            />
          </div>

          <label className="flex items-center gap-2 text-sm text-slate-700 dark:text-slate-200 cursor-pointer">
            <input
              type="checkbox"
              checked={recorrenteAnual}
              onChange={(e) => setRecorrenteAnual(e.target.checked)}
              className="accent-blue-600"
            />
            <span>Repetir todo ano (aniversário, feriado fixo)</span>
          </label>

          {erroMsg && (
            <div className="bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-700 text-red-700 dark:text-red-300 px-3 py-2 rounded text-xs">
              {erroMsg}
            </div>
          )}
        </div>

        <div className="px-5 py-3 border-t border-slate-200 dark:border-slate-700 flex justify-between gap-2">
          {modoEdicao && inicial.id ? (
            <button
              type="button"
              onClick={handleDeletar}
              disabled={deletarM.isPending}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 disabled:opacity-50"
              title="Excluir evento"
            >
              <Trash2 size={14} /> Excluir
            </button>
          ) : <span />}
          <div className="flex gap-2">
            <button onClick={onClose} className="px-3 py-1.5 rounded text-sm bg-slate-100 dark:bg-slate-700 hover:bg-slate-200 dark:hover:bg-slate-600 text-slate-700 dark:text-slate-200">
              Cancelar
            </button>
            <button
              onClick={handleSubmit}
              disabled={!podeSalvar || submitting}
              className="flex items-center gap-1.5 px-4 py-1.5 rounded text-sm bg-blue-600 hover:bg-blue-700 text-white disabled:opacity-50"
            >
              <Pencil size={14} /> {submitting ? "Salvando…" : modoEdicao ? "Salvar" : "Criar"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// =============================================================================
// Config — gerenciar aniversários
// =============================================================================
export function ConfigCalendarioModal({ onClose }: { onClose: () => void }) {
  const qc = useQueryClient();
  const [aba, setAba] = useState<"aniversarios" | "lembretes">("aniversarios");

  const tabs: Array<{ id: "aniversarios" | "lembretes"; label: string; icon: any; cor: string }> = [
    { id: "aniversarios", label: "Aniversários", icon: Cake, cor: "border-pink-500 text-pink-700 dark:text-pink-400" },
    { id: "lembretes",    label: "Lembretes",    icon: Bell, cor: "border-blue-500 text-blue-700 dark:text-blue-400" },
  ];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={onClose}>
      <div className="bg-white dark:bg-slate-800 rounded-xl w-full max-w-2xl max-h-[92vh] overflow-hidden shadow-2xl flex flex-col" onClick={(e) => e.stopPropagation()}>
        <div className="px-5 py-3 border-b border-slate-200 dark:border-slate-700 flex items-center justify-between">
          <h3 className="font-semibold text-slate-800 dark:text-slate-100 flex items-center gap-2">
            <Settings size={16} /> Configurações do Calendário
          </h3>
          <button onClick={onClose} className="p-1.5 rounded hover:bg-slate-100 dark:hover:bg-slate-700 text-slate-500 dark:text-slate-400">
            <X size={18} />
          </button>
        </div>

        <div className="flex border-b border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900/40">
          {tabs.map((t) => {
            const Icon = t.icon;
            const ativa = aba === t.id;
            return (
              <button
                key={t.id}
                type="button"
                onClick={() => setAba(t.id)}
                className={`flex items-center gap-1.5 px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${
                  ativa
                    ? t.cor
                    : "border-transparent text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-200"
                }`}
              >
                <Icon size={14} /> {t.label}
              </button>
            );
          })}
        </div>

        <div className="flex-1 overflow-y-auto">
          {aba === "aniversarios" && <AbaAniversarios onMutar={() => qc.invalidateQueries({ queryKey: ["calendario-eventos"] })} />}
          {aba === "lembretes" && <AbaLembretes />}
        </div>

        <div className="px-5 py-3 border-t border-slate-200 dark:border-slate-700 flex justify-end">
          <button onClick={onClose} className="px-4 py-1.5 rounded text-sm bg-slate-100 dark:bg-slate-700 hover:bg-slate-200 dark:hover:bg-slate-600 text-slate-700 dark:text-slate-200">
            Fechar
          </button>
        </div>
      </div>
    </div>
  );
}

function AbaLembretes() {
  const [ativo, setAtivo] = useState(getLembreteAtivo());
  const [antecedencia, setAntecedencia] = useState(getLembreteAntecedencia());
  const [permission, setPermission] = useState<NotificationPermission>(
    typeof Notification !== "undefined" ? Notification.permission : "denied",
  );

  function toggleAtivo(v: boolean) {
    setAtivo(v);
    setLembreteAtivo(v);
    // Se ligando e ainda não permitiu, pede.
    if (v && permission !== "granted" && typeof Notification !== "undefined") {
      Notification.requestPermission().then((p) => setPermission(p));
    }
  }
  function trocarAntecedencia(v: number) {
    setAntecedencia(v);
    setLembreteAntecedencia(v);
  }
  function pedirPermissao() {
    if (typeof Notification === "undefined") return;
    Notification.requestPermission().then((p) => setPermission(p));
  }
  function testar() {
    if (permission !== "granted") return;
    try {
      new Notification("🔔 Teste de lembrete", {
        body: "É assim que os lembretes vão aparecer.",
        tag: "cal-teste",
      });
    } catch {}
  }

  const semSuporte = typeof Notification === "undefined";

  return (
    <div className="p-5 space-y-4">
      <div className="flex items-start gap-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-3">
        <BellRing size={18} className="text-blue-600 dark:text-blue-400 shrink-0 mt-0.5" />
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-blue-800 dark:text-blue-200">Lembretes de eventos</p>
          <p className="text-xs text-blue-700/80 dark:text-blue-300/80">
            Notificação do navegador antes de aniversários, reuniões, podcasts e demais eventos.
            Eventos sem horário definido lembram no início do dia (08:00).
          </p>
        </div>
      </div>

      {semSuporte && (
        <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 text-amber-800 dark:text-amber-200 px-3 py-2 rounded text-xs">
          Seu navegador não suporta notificações.
        </div>
      )}

      <label className="flex items-start gap-3 p-3 rounded-lg border border-slate-200 dark:border-slate-700 cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-700/30">
        <input
          type="checkbox"
          checked={ativo}
          onChange={(e) => toggleAtivo(e.target.checked)}
          disabled={semSuporte}
          className="mt-0.5 accent-blue-600"
        />
        <div className="flex-1">
          <p className="text-sm font-medium text-slate-800 dark:text-slate-100">Ativar lembretes</p>
          <p className="text-xs text-slate-500 dark:text-slate-400">
            Por padrão desativado. Quando ligado, você recebe um aviso do navegador antes de cada evento.
          </p>
        </div>
      </label>

      <div className={`${ativo ? "" : "opacity-50 pointer-events-none"}`}>
        <label className="text-xs text-slate-600 dark:text-slate-400 block mb-1">Antecedência</label>
        <select
          value={antecedencia}
          onChange={(e) => trocarAntecedencia(Number(e.target.value))}
          className="w-full px-3 py-2 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-800 dark:text-slate-100 text-sm focus:border-blue-500 outline-none"
        >
          {ANTECEDENCIA_OPCOES.map((o) => (
            <option key={o.valor} value={o.valor}>{o.label}</option>
          ))}
        </select>
      </div>

      {!semSuporte && (
        <div className="border-t border-slate-200 dark:border-slate-700 pt-3 space-y-2">
          <p className="text-xs text-slate-600 dark:text-slate-400">
            Status da permissão do navegador:{" "}
            <strong className={
              permission === "granted" ? "text-emerald-600 dark:text-emerald-400" :
              permission === "denied" ? "text-red-600 dark:text-red-400" :
              "text-amber-600 dark:text-amber-400"
            }>
              {permission === "granted" ? "permitido" : permission === "denied" ? "bloqueado" : "ainda não solicitado"}
            </strong>
          </p>
          {permission !== "granted" && (
            <button
              type="button"
              onClick={pedirPermissao}
              disabled={permission === "denied"}
              className="text-xs bg-blue-600 hover:bg-blue-700 text-white px-3 py-1.5 rounded disabled:opacity-50 disabled:cursor-not-allowed"
              title={permission === "denied" ? "Bloqueado nas configs do navegador — desbloqueie por lá" : ""}
            >
              {permission === "denied" ? "Bloqueado pelo navegador" : "Permitir notificações"}
            </button>
          )}
          {permission === "granted" && ativo && (
            <button
              type="button"
              onClick={testar}
              className="text-xs bg-slate-100 dark:bg-slate-700 hover:bg-slate-200 dark:hover:bg-slate-600 text-slate-700 dark:text-slate-200 px-3 py-1.5 rounded border border-slate-300 dark:border-slate-600"
            >
              Disparar notificação de teste
            </button>
          )}
        </div>
      )}
    </div>
  );
}

function AbaAniversarios({ onMutar }: { onMutar: () => void }) {
  const qc = useQueryClient();
  const [novoNome, setNovoNome] = useState("");
  const [novoDia, setNovoDia] = useState("");
  const [novoMes, setNovoMes] = useState("");
  const [editId, setEditId] = useState<string | null>(null);
  const [editNome, setEditNome] = useState("");
  const [editDia, setEditDia] = useState("");
  const [editMes, setEditMes] = useState("");

  const { data: lista = [], isLoading } = useQuery({
    queryKey: ["calendario-aniversarios"],
    queryFn: listarAniversarios,
  });

  function invalidate() {
    qc.invalidateQueries({ queryKey: ["calendario-aniversarios"] });
    onMutar();
  }

  const criarM = useMutation({
    mutationFn: (input: { nome: string; dia: number; mes: number }) =>
      criarEventoCalendario({
        titulo: `Aniversário ${input.nome}`,
        data: `2000-${String(input.mes).padStart(2, "0")}-${String(input.dia).padStart(2, "0")}`,
        tipo: "aniversario",
        recorrenteAnual: true,
      }),
    onSuccess: () => {
      setNovoNome(""); setNovoDia(""); setNovoMes("");
      invalidate();
    },
  });

  const editarM = useMutation({
    mutationFn: (input: { id: string; nome: string; dia: number; mes: number }) =>
      atualizarEventoCalendario(input.id, {
        titulo: `Aniversário ${input.nome}`,
        data: `2000-${String(input.mes).padStart(2, "0")}-${String(input.dia).padStart(2, "0")}`,
      }),
    onSuccess: () => {
      setEditId(null);
      invalidate();
    },
  });

  const deletarM = useMutation({
    mutationFn: (id: string) => deletarEventoCalendario(id),
    onSuccess: invalidate,
  });

  function nomeDoEvento(titulo: string) {
    return titulo.replace(/^Aniversário\s+/i, "").trim() || titulo;
  }

  function podeAdicionar() {
    const dia = parseInt(novoDia, 10);
    const mes = parseInt(novoMes, 10);
    return novoNome.trim().length > 0 && dia >= 1 && dia <= 31 && mes >= 1 && mes <= 12;
  }
  function podeSalvarEdit() {
    const dia = parseInt(editDia, 10);
    const mes = parseInt(editMes, 10);
    return editNome.trim().length > 0 && dia >= 1 && dia <= 31 && mes >= 1 && mes <= 12;
  }

  return (
    <div className="p-5 space-y-4">
      <div className="flex items-start gap-3 bg-pink-50 dark:bg-pink-900/20 border border-pink-200 dark:border-pink-800 rounded-lg p-3">
        <Cake size={18} className="text-pink-600 dark:text-pink-400 shrink-0 mt-0.5" />
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-pink-800 dark:text-pink-200">Aniversários da equipe</p>
          <p className="text-xs text-pink-700/80 dark:text-pink-300/80">
            Repetem todo ano automaticamente. Visíveis para todos no calendário.
          </p>
        </div>
      </div>

      {/* Adicionar novo */}
      <div className="border border-slate-200 dark:border-slate-700 rounded-lg p-3">
        <p className="text-xs font-semibold uppercase tracking-wide text-slate-600 dark:text-slate-300 mb-2">Adicionar aniversário</p>
        <div className="grid grid-cols-12 gap-2">
          <input
            type="text"
            value={novoNome}
            onChange={(e) => setNovoNome(e.target.value)}
            placeholder="Nome (ex.: Diogo)"
            className="col-span-7 px-3 py-2 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-800 dark:text-slate-100 text-sm focus:border-blue-500 outline-none"
          />
          <input
            type="number"
            min={1}
            max={31}
            value={novoDia}
            onChange={(e) => setNovoDia(e.target.value)}
            placeholder="Dia"
            className="col-span-2 px-3 py-2 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-800 dark:text-slate-100 text-sm focus:border-blue-500 outline-none tabular-nums"
          />
          <input
            type="number"
            min={1}
            max={12}
            value={novoMes}
            onChange={(e) => setNovoMes(e.target.value)}
            placeholder="Mês"
            className="col-span-2 px-3 py-2 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-800 dark:text-slate-100 text-sm focus:border-blue-500 outline-none tabular-nums"
          />
          <button
            type="button"
            onClick={() => criarM.mutate({ nome: novoNome.trim(), dia: parseInt(novoDia, 10), mes: parseInt(novoMes, 10) })}
            disabled={!podeAdicionar() || criarM.isPending}
            className="col-span-1 flex items-center justify-center bg-blue-600 hover:bg-blue-700 text-white rounded-lg disabled:opacity-50"
            title="Adicionar"
          >
            <Plus size={16} />
          </button>
        </div>
      </div>

      {/* Lista */}
      <div>
        <p className="text-xs font-semibold uppercase tracking-wide text-slate-600 dark:text-slate-300 mb-2">
          Cadastrados ({lista.length})
        </p>
        {isLoading ? (
          <p className="text-sm text-slate-400 dark:text-slate-500 text-center py-6">Carregando…</p>
        ) : lista.length === 0 ? (
          <p className="text-sm text-slate-400 dark:text-slate-500 text-center py-6">
            Nenhum aniversário cadastrado. Use "Popular lista inicial" pra começar com a galera padrão.
          </p>
        ) : (
          <ul className="space-y-1.5">
            {lista.map((a) => {
              const editando = editId === a.id;
              return (
                <li
                  key={a.id}
                  className="flex items-center gap-2 px-3 py-2 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800"
                >
                  <span className="text-base">🎂</span>
                  {editando ? (
                    <>
                      <input
                        type="text"
                        value={editNome}
                        onChange={(e) => setEditNome(e.target.value)}
                        className="flex-1 px-2 py-1 rounded border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-700 text-sm text-slate-800 dark:text-slate-100"
                      />
                      <input
                        type="number"
                        min={1} max={31}
                        value={editDia}
                        onChange={(e) => setEditDia(e.target.value)}
                        className="w-14 px-2 py-1 rounded border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-700 text-sm text-slate-800 dark:text-slate-100 tabular-nums text-center"
                      />
                      <input
                        type="number"
                        min={1} max={12}
                        value={editMes}
                        onChange={(e) => setEditMes(e.target.value)}
                        className="w-14 px-2 py-1 rounded border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-700 text-sm text-slate-800 dark:text-slate-100 tabular-nums text-center"
                      />
                      <button
                        type="button"
                        onClick={() => editarM.mutate({ id: a.id, nome: editNome.trim(), dia: parseInt(editDia, 10), mes: parseInt(editMes, 10) })}
                        disabled={!podeSalvarEdit() || editarM.isPending}
                        className="text-xs bg-blue-600 hover:bg-blue-700 text-white px-2 py-1 rounded disabled:opacity-50"
                      >
                        Salvar
                      </button>
                      <button
                        type="button"
                        onClick={() => setEditId(null)}
                        className="text-xs px-2 py-1 rounded text-slate-500 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-700"
                      >
                        Cancelar
                      </button>
                    </>
                  ) : (
                    <>
                      <span className="flex-1 text-sm text-slate-800 dark:text-slate-100">
                        {nomeDoEvento(a.titulo)}
                        {a.colaboradorNome && (
                          <span className="ml-2 text-[11px] text-slate-400 dark:text-slate-500">vinculado: {a.colaboradorNome}</span>
                        )}
                      </span>
                      <span className="text-xs font-medium tabular-nums text-slate-600 dark:text-slate-300 bg-slate-100 dark:bg-slate-700 px-2 py-0.5 rounded">
                        {String(a.dia).padStart(2, "0")}/{String(a.mes).padStart(2, "0")}
                      </span>
                      <button
                        type="button"
                        onClick={() => {
                          setEditId(a.id);
                          setEditNome(nomeDoEvento(a.titulo));
                          setEditDia(String(a.dia));
                          setEditMes(String(a.mes));
                        }}
                        className="p-1 rounded text-slate-400 dark:text-slate-500 hover:text-blue-600 dark:hover:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/30"
                        title="Editar"
                      >
                        <Pencil size={13} />
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          if (window.confirm(`Excluir aniversário de ${nomeDoEvento(a.titulo)}?`)) deletarM.mutate(a.id);
                        }}
                        className="p-1 rounded text-slate-400 dark:text-slate-500 hover:text-red-600 dark:hover:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/30"
                        title="Excluir"
                      >
                        <Trash2 size={13} />
                      </button>
                    </>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
}
